const chapter = {
  id: 'molecular-basis-inheritance',
  slug: 'molecular-basis-inheritance',
  subject: 'biology',
  subjectLabel: 'Biology',
  exam: 'NEET',
  title: {
    english: 'Molecular Basis of Inheritance',
        hindi: 'वंशागति का आण्विक आधार',
  },
  estReadMinutes: 65,
  difficulty: {
    english: 'Hard',
        hindi: 'कठिन',
  },
  weightage: {
    english: '4-5 questions in NEET',
        hindi: 'NEET में 4-5 प्रश्न **संगठन का स्तर** — सेलुलर (पोरिफेरा) | कोशिका-ऊतक (सीलेंटरेटा) | ऊतक-अंग (प्लैटिहेल्मिन्थेस) | अंग-प्रणाली (एनेलिडा से आगे)।',
  },
  syllabusTags: ['Genetics', 'Molecular Biology', 'NEET', 'Class-12'],
  sections: [
    {
      id: 'dna-structure',
      title: {
        english: 'DNA — Structure',
        hindi: 'DNA — संरचना',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Polynucleotide of deoxyribonucleotides. Each nucleotide = nitrogen base + deoxyribose sugar + phosphate.',
        hindi: 'डीऑक्सीराइबोन्यूक्लियोटाइड्स के पॉलीन्यूक्लियोटाइड। प्रत्येक न्यूक्लियोटाइड = नाइट्रोजन बेस + डीऑक्सीराइबोज शुगर + फॉस्फेट।',
            },
            {
              english: '**Bases**: Purines = Adenine (A), Guanine (G); Pyrimidines = Cytosine (C), Thymine (T) [Uracil U in RNA].',
        hindi: '**आधार**: Purines = Adenine (A), गुआनिन (जी); Pyrimidines = Cytosine (C), थाइमिन (टी) [आरएनए में यूरेसिल यू]।',
            },
            {
              english: '**Phosphodiester bond** — between 3′-OH and 5′-phosphate; gives strand polarity.',
        hindi: '**फॉस्फोडिएस्टर बंधन** - 3′-OH और 5′-फॉस्फेट के बीच; स्ट्रैंड को ध्रुवीयता देता है।',
            },
            {
              english: '**Watson & Crick (1953)** — double helix model based on Wilkins & Franklin\'s X-ray data; right-handed; antiparallel strands.',
        hindi: '**वाटसन और क्रिक (1953)** - विल्किंस और फ्रैंकलिन के एक्स-रे डेटा पर आधारित डबल हेलिक्स मॉडल; दांए हाथ से काम करने वाला; प्रतिसमानांतर किस्में।',
            },
            {
              english: '• Diameter 20 Å (2 nm); pitch 34 Å; **10 bp per turn**; **rise per bp = 3.4 Å**.',
        hindi: '• व्यास 20 Å (2 एनएम); पिच 34 Å; **10 बीपी प्रति मोड़**; **प्रति bp = 3.4 Å** वृद्धि।',
            },
            {
              english: '• **Chargaff\'s rule**: $A = T$, $G equiv C$; $A+G = T+C$ (purines = pyrimidines).',
        hindi: '• **चार्गाफ़ का नियम**: $A = T$, $G समतुल्य C$; $ए+जी = टी+सी$ (प्यूरीन = पाइरीमिडीन)।',
            },
            {
              english: 'Hydrogen bonds: $A=T$ (2 H-bonds), $Gequiv C$ (3 H-bonds).',
        hindi: 'हाइड्रोजन बांड: $A=T$ (2 H-बॉन्ड), $Gequiv C$ (3 H-बॉन्ड)।',
            },
            {
              english: '**Length of DNA** = $(\\text{number of bp}) \\times 3.4\\ \\text{\\AA}$. Human haploid genome $\\approx 3.3 \\times 10^9\\ \\text{bp} \\approx 2.2\\ \\text{m}$.',
        hindi: '**डीएनए की लंबाई** = $(\\text{number of bp}) \\times 3.4\\ \\text{\\AA}$. मानव अगुणित जीनोम $\\approx 3.3 \\times 10^9\\ \\text{bp} \\approx 2.2\\ \\text{m}$.',
            },
            {
              english: '**Packaging**: DNA + histone octamer (2 each of H2A, H2B, H3, H4) → **nucleosome** (200 bp); H1 acts as linker. Nucleosomes coil into chromatin → chromatids → chromosome.',
        hindi: '**पैकेजिंग**: डीएनए + हिस्टोन ऑक्टेमर (एच2ए, एच2बी, एच3, एच4 में से प्रत्येक 2) → **न्यूक्लियोसोम** (200 बीपी); H1 लिंकर के रूप में कार्य करता है। न्यूक्लियोसोम क्रोमैटिन → क्रोमैटिड्स → क्रोमोसोम में कुंडलित होते हैं।',
            },
          ],
        },
      ],
    },
    {
      id: 'dna-as-genetic-material',
      title: {
        english: 'Search for Genetic Material',
        hindi: 'आनुवंशिक सामग्री की खोज',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Griffith (1928)** — Transformation experiment with *Streptococcus pneumoniae*. Heat-killed S strain + live R strain → mice die; live S recovered. Concluded "transforming principle".',
        hindi: '**ग्रिफ़िथ (1928)** - *स्ट्रेप्टोकोकस निमोनिया* के साथ परिवर्तन प्रयोग। गर्मी से मारे गए एस स्ट्रेन + जीवित आर स्ट्रेन → चूहे मर जाते हैं; लाइव एस बरामद. "परिवर्तनकारी सिद्धांत" का निष्कर्ष निकाला गया।',
            },
            {
              english: '**Avery, MacLeod, McCarty (1933-44)** — biochemical characterisation: only DNase destroyed transforming activity → **DNA is the transforming principle**.',
        hindi: '**एवरी, मैकलियोड, मैककार्टी (1933-44)** - जैव रासायनिक लक्षण वर्णन: केवल DNase ने परिवर्तनकारी गतिविधि को नष्ट कर दिया → **डीएनए परिवर्तनकारी सिद्धांत है**।',
            },
            {
              english: '**Hershey & Chase (1952)** — used T2 bacteriophage; labelled **DNA with ³²P, protein with ³⁵S**. After infection, only ³²P entered bacteria → **DNA is the genetic material**.',
        hindi: '**हर्शे एंड चेज़ (1952)** — प्रयुक्त टी2 बैक्टीरियोफेज; **डीएनए को ³²P, प्रोटीन को ³⁵S** के साथ लेबल किया गया। संक्रमण के बाद, केवल ³²P बैक्टीरिया में प्रवेश करता है → **डीएनए आनुवंशिक सामग्री है**।',
            },
            {
              english: '**Why DNA, not RNA?** DNA is more stable (deoxyribose lacks 2′-OH); double-stranded; thymine instead of uracil (less mutable).',
        hindi: '**डीएनए क्यों, आरएनए नहीं?** डीएनए अधिक स्थिर है (डीऑक्सीराइबोज़ में 2′-OH की कमी है); दोहरी मुसीबत; यूरैसिल के स्थान पर थाइमिन (कम परिवर्तनशील)।',
            },
            {
              english: 'In some viruses (TMV, HIV, QB phage) **RNA is the genetic material** but DNA is preferred for storage in cellular organisms.',
        hindi: 'कुछ वायरस (टीएमवी, एचआईवी, क्यूबी फेज) में **आरएनए आनुवंशिक सामग्री है** लेकिन सेलुलर जीवों में भंडारण के लिए डीएनए को प्राथमिकता दी जाती है।',
            },
          ],
        },
      ],
    },
    {
      id: 'replication',
      title: {
        english: 'DNA Replication',
        hindi: 'DNA प्रतिकृति',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Semi-conservative** — proven by **Meselson & Stahl (1958)** on *E. coli* using ¹⁵N → ¹⁴N labelling and CsCl density gradient. After 1 generation: hybrid band; after 2: hybrid + light.',
        hindi: '**अर्ध-रूढ़िवादी** - **मेसेलसन और स्टाल (1958)** द्वारा *ई. पर सिद्ध। कोलाई* ¹⁵N → ¹⁴N लेबलिंग और CsCl घनत्व ग्रेडिएंट का उपयोग करके। 1 पीढ़ी के बाद: हाइब्रिड बैंड; 2 के बाद: संकर + प्रकाश।',
            },
            {
              english: '**Taylor et al. (1958)** demonstrated semi-conservative replication in *Vicia faba* (broad bean) using ³H-thymidine.',
        hindi: '**टेलर एट अल. (1958)** ने ³H-थाइमिडीन का उपयोग करके *विकिया फैबा* (ब्रॉड बीन) में अर्ध-रूढ़िवादी प्रतिकृति का प्रदर्शन किया।',
            },
            {
              english: '**Origin (ori)** — site where replication initiates (single in prokaryotes, multiple in eukaryotes).',
        hindi: '**उत्पत्ति (ओआरआई)** - वह स्थान जहां प्रतिकृति आरंभ होती है (प्रोकैरियोट्स में एकल, यूकेरियोट्स में एकाधिक)।',
            },
            {
              english: '**Replication fork** — Y-shaped; replication is bidirectional.',
        hindi: '**प्रतिकृति कांटा** - वाई-आकार; प्रतिकृति द्विदिशात्मक है.',
            },
            {
              english: '**Direction**: DNA polymerase synthesises only **5′ → 3′**. Hence:',
        hindi: '**दिशा**: डीएनए पोलीमरेज़ केवल **5′ → 3′** का संश्लेषण करता है। इस तरह:',
            },
            {
              english: '• **Leading strand** — continuous synthesis (3′→5′ template).',
        hindi: '• **अग्रणी स्ट्रैंड** — निरंतर संश्लेषण (3′→5′ टेम्पलेट)।',
            },
            {
              english: '• **Lagging strand** — discontinuous; short fragments (**Okazaki fragments**) joined by **DNA ligase**.',
        hindi: '• **लैगिंग स्ट्रैंड** — असंतुलित; छोटे टुकड़े (**ओकाज़ाकी टुकड़े**) **डीएनए लिगेज** से जुड़े हुए। **मुख्य एंजाइम**:',
            },
            {
              english: '**Key enzymes**:',
        hindi: '**प्रमुख एंजाइम**:',
            },
            {
              english: '• **Helicase** — unwinds DNA at fork.',
        hindi: '• **हेलिकेज़** - डीएनए को फोर्क पर खोलता है।',
            },
            {
              english: '• **Topoisomerase / gyrase** — relieves supercoiling.',
        hindi: '• **टोपोइज़ोमेरेज़ / गाइरेज़** - सुपरकोलिंग से राहत देता है।',
            },
            {
              english: '• **Primase** — synthesises RNA primer.',
        hindi: '• **प्राइमेज़** - आरएनए प्राइमर को संश्लेषित करता है।',
            },
            {
              english: '• **DNA Polymerase III** (E. coli) — main polymerisation; very high fidelity & speed (~2000 bp/s).',
        hindi: '• **डीएनए पोलीमरेज़ III** (ई. कोलाई) — मुख्य पोलीमराइजेशन; बहुत उच्च निष्ठा और गति (~2000 bp/s)।',
            },
            {
              english: '• **DNA Polymerase I** — removes RNA primer, fills gaps.',
        hindi: '• **डीएनए पॉलीमरेज़ I** - आरएनए प्राइमर को हटाता है, अंतराल भरता है।',
            },
            {
              english: '• **DNA Ligase** — seals nicks.',
        hindi: '• **डीएनए लिगेज** - सील निक्स।',
            },
            {
              english: 'Energy comes from **dNTPs** (deoxyribonucleoside triphosphates) — substrates and energy donors.',
        hindi: 'ऊर्जा **dNTPs** (डीऑक्सीराइबोन्यूक्लियोसाइड ट्राइफॉस्फेट) से आती है - सब्सट्रेट और ऊर्जा दाताओं।',
            },
          ],
        },
      ],
    },
    {
      id: 'transcription',
      title: {
        english: 'Transcription',
        hindi: 'अनुलेखन',
      },
      blocks: [
        {
          type: 'paragraph',
          text: {
            english: 'DNA → RNA. Only **template strand** (3′→5′) is transcribed; the **coding/sense strand** has the same sequence as mRNA (with T instead of U).',
        hindi: 'डीएनए → आरएनए। केवल **टेम्पलेट स्ट्रैंड** (3′→5′) प्रतिलेखित है; **coding/sense स्ट्रैंड** का अनुक्रम mRNA के समान है (U के बजाय T के साथ)।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: '**Transcription unit**: Promoter (upstream) — Structural gene — Terminator (downstream).',
        hindi: '**प्रतिलेखन इकाई**: प्रमोटर (अपस्ट्रीम) - संरचनात्मक जीन - टर्मिनेटर (डाउनस्ट्रीम)।',
            },
            {
              english: '**Prokaryotes** — single **RNA polymerase** transcribes all 3 RNAs (mRNA, tRNA, rRNA). σ factor for initiation; ρ factor for termination. mRNA is **polycistronic** and does not need processing.',
        hindi: '**प्रोकैरियोट्स** - एकल **आरएनए पोलीमरेज़** सभी 3 आरएनए (एमआरएनए, टीआरएनए, आरआरएनए) को ट्रांसक्राइब करता है। दीक्षा के लिए σ कारक; समाप्ति के लिए ρ कारक। एमआरएनए **पॉलीसिस्ट्रोनिक** है और इसे प्रसंस्करण की आवश्यकता नहीं है।',
            },
            {
              english: '**Eukaryotes** — three RNA polymerases:',
        hindi: '**यूकेरियोट्स** - तीन आरएनए पोलीमरेज़:',
            },
            {
              english: '• **RNA Pol I** → rRNA (28S, 18S, 5.8S).',
        hindi: '• **आरएनए पोल I** → आरआरएनए (28एस, 18एस, 5.8एस)।',
            },
            {
              english: '• **RNA Pol II** → hnRNA (precursor of mRNA).',
        hindi: '• **आरएनए पोल II** → एचएनआरएनए (एमआरएनए का अग्रदूत)।',
            },
            {
              english: '• **RNA Pol III** → tRNA, 5S rRNA, snRNA.',
        hindi: '• **आरएनए पोल III** → टीआरएनए, 5एस आरआरएनए, एसएनआरएनए।',
            },
            {
              english: '**hnRNA processing** — (i) **5′ capping** (methyl guanosine triphosphate); (ii) **3′ polyadenylation** (poly-A tail, ~200 A); (iii) **splicing** — introns (non-coding) removed by **spliceosome**, exons (coding) joined.',
        hindi: '**एचएनआरएनए प्रसंस्करण** - (i) **5′ कैपिंग** (मिथाइल ग्वानोसिन ट्राइफॉस्फेट); (ii) **3′ पॉलीएडेनाइलेशन** (पॉली-ए टेल, ~200 ए); (iii) **स्प्लिसिंग** - **स्प्लिसियोसोम** द्वारा इंट्रोन्स (नॉन-कोडिंग) को हटा दिया गया, एक्सॉन (कोडिंग) शामिल हो गए।',
            },
            {
              english: 'Eukaryote mRNA = **monocistronic**; gene is **split (introns + exons)**.',
        hindi: 'यूकेरियोट mRNA = **monocistronic**; जीन **विभाजित (इंट्रॉन + एक्सॉन)** है।',
            },
          ],
        },
      ],
    },
    {
      id: 'genetic-code',
      title: {
        english: 'Genetic Code & Translation',
        hindi: 'आनुवंशिक कूट और अनुवाद',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Codon** = triplet of nucleotides on mRNA → one amino acid.',
        hindi: '**कोडोन** = एमआरएनए पर न्यूक्लियोटाइड का त्रिक → एक अमीनो एसिड।',
            },
            {
              english: '**George Gamow** — proposed triplet code; **Khorana, Nirenberg, Holley** deciphered it (Nobel 1968).',
        hindi: '**जॉर्ज गामो** - प्रस्तावित ट्रिपलेट कोड; **खोराना, निरेनबर्ग, होली** ने इसे समझा (नोबेल 1968)।',
            },
            {
              english: '64 codons total: **61 sense + 3 stop** (UAA, UAG, UGA — *amber, ochre, opal*). **AUG** = start codon, also codes Methionine.',
        hindi: 'कुल 64 कोडन: **61 सेंस + 3 स्टॉप** (यूएए, यूएजी, यूजीए - *एम्बर, गेरू, ओपल*)। **अगस्त** = प्रारंभ कोडन, मेथियोनीन को भी कोडित करता है।',
            },
            {
              english: 'Salient features:',
        hindi: 'मुख्य विशेषताएं:',
            },
            {
              english: '• Triplet, non-overlapping, comma-less.',
        hindi: '• त्रिक, गैर-अतिव्यापी, अल्पविराम-रहित।',
            },
            {
              english: '• **Universal** (with minor exceptions in mitochondria).',
        hindi: '• **यूनिवर्सल** (माइटोकॉन्ड्रिया में मामूली अपवादों के साथ)।',
            },
            {
              english: '• **Degenerate** — most amino acids coded by more than one codon (only Met, Trp have one).',
        hindi: '• **डीजेनरेट** - अधिकांश अमीनो एसिड एक से अधिक कोडन द्वारा कोडित होते हैं (केवल मेट, टीआरपी में एक होता है)।',
            },
            {
              english: '• **Unambiguous** — one codon → one amino acid.',
        hindi: '• **स्पष्ट** - एक कोडन → एक अमीनो एसिड।',
            },
            {
              english: '**tRNA** (Holley\'s clover-leaf, actually L-shaped) — adapter molecule. Has **anticodon loop** (reads codon) and acceptor end (3′ CCA — binds amino acid). Initiator tRNA carries Met (formyl-Met in prokaryotes).',
        hindi: '**tRNA** (होली का तिपतिया घास-पत्ता, वास्तव में एल-आकार) - एडाप्टर अणु। **एंटीकोडोन लूप** (कोडन पढ़ता है) और स्वीकर्ता अंत (3′ सीसीए - अमीनो एसिड को बांधता है) है। सर्जक टीआरएनए मेट (प्रोकैरियोट्स में फॉर्माइल-मेट) ले जाता है। **अनुवाद** — तीन चरण:',
            },
            {
              english: '**Translation** — three stages:',
        hindi: '**अनुवाद** — तीन चरण:',
            },
            {
              english: '• **Initiation** — ribosome (small + large subunit) assembles on mRNA at AUG; initiator tRNA at P-site.',
        hindi: '• **आरंभ** - राइबोसोम (छोटी + बड़ी सबयूनिट) AUG पर mRNA पर एकत्रित होती है; पी-साइट पर आरंभकर्ता टीआरएनए।',
            },
            {
              english: '• **Elongation** — codon-anticodon pairing at A-site; **peptidyl transferase** (rRNA — ribozyme) forms peptide bond; translocation by EF-G.',
        hindi: '• ** बढ़ाव** - ए-साइट पर कोडन-एंटीकोडोन युग्मन; **पेप्टिडाइल ट्रांसफ़ेज़** (आरआरएनए - राइबोजाइम) पेप्टाइड बॉन्ड बनाता है; ईएफ-जी द्वारा स्थानांतरण।',
            },
            {
              english: '• **Termination** — release factor recognises stop codon; polypeptide released.',
        hindi: '• **समाप्ति** - रिलीज फैक्टर स्टॉप कोडन को पहचानता है; पॉलीपेप्टाइड जारी किया गया।',
            },
          ],
        },
      ],
    },
    {
      id: 'lac-operon',
      title: {
        english: 'Regulation — lac Operon',
        hindi: 'नियमन — lac ऑपेरॉन',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Jacob & Monod (1961)** — proposed operon model in *E. coli* for lactose metabolism.',
        hindi: '**जैकब और मोनोड (1961)** - *ई में प्रस्तावित ऑपेरॉन मॉडल। लैक्टोज चयापचय के लिए कोलाई*।',
            },
            {
              english: 'Components: regulator gene **i** (codes repressor) — promoter **P** — operator **O** — structural genes **z (β-galactosidase), y (permease), a (transacetylase)**.',
        hindi: 'घटक: नियामक जीन **i** (कोड रिप्रेसर) - प्रमोटर **P** - ऑपरेटर **O** - संरचनात्मक जीन **z (β-galactosidase), y (permease), a (transacetylase)**।',
            },
            {
              english: '**Without lactose** — repressor binds operator → RNA Pol blocked → no transcription.',
        hindi: '**लैक्टोज के बिना** - रिप्रेसर ऑपरेटर को बांधता है → आरएनए पोल अवरुद्ध → कोई प्रतिलेखन नहीं।',
            },
            {
              english: '**With lactose (inducer)** — lactose binds repressor → repressor inactivated → operon transcribed; enzymes produced break down lactose.',
        hindi: '**लैक्टोज (प्रेरक) के साथ** - लैक्टोज दमनकर्ता को बांधता है → दमनकर्ता निष्क्रिय → ऑपेरॉन प्रतिलेखित; उत्पादित एंजाइम लैक्टोज को तोड़ते हैं।',
            },
            {
              english: '**Negative inducible regulation** (substrate switches gene ON).',
        hindi: '**नकारात्मक प्रेरक विनियमन** (सब्सट्रेट जीन को चालू करता है)।',
            },
          ],
        },
      ],
    },
    {
      id: 'hgp',
      title: {
        english: 'Human Genome Project & DNA Fingerprinting',
        hindi: 'मानव जीनोम परियोजना और DNA अंगुली-छाप',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**HGP (1990-2003)** — international, about 3 billion USD, 13 years. Goals: identify all genes, determine 3 billion bp sequence, store in databases.',
        hindi: '**एचजीपी (1990-2003)** - अंतर्राष्ट्रीय, लगभग 3 बिलियन अमरीकी डालर, 13 वर्ष। लक्ष्य: सभी जीनों की पहचान करना, 3 बिलियन बीपी अनुक्रम निर्धारित करना, डेटाबेस में संग्रहित करना।',
            },
            {
              english: 'Salient findings: ~3.1 × 10⁹ bp; ~30,000 genes (far fewer than expected); average gene 3000 bp; chromosome 1 has most genes (2968), Y has least (231). >50 % genome is **repetitive sequences**.',
        hindi: 'मुख्य निष्कर्ष: ~3.1 × 10⁹ बीपी; ~30,000 जीन (उम्मीद से बहुत कम); औसत जीन 3000 बीपी; गुणसूत्र 1 में सबसे अधिक जीन (2968) हैं, Y में सबसे कम (231) हैं। >50% जीनोम **दोहरावदार अनुक्रम** है।',
            },
            {
              english: '**DNA fingerprinting** — **Alec Jeffreys (1985)**. Uses **VNTRs (Variable Number Tandem Repeats)** / minisatellites that vary in copy number among individuals.',
        hindi: '**डीएनए फ़िंगरप्रिंटिंग** — **एलेक जेफ़्रीज़ (1985)**। **वीएनटीआर (वैरिएबल नंबर टेंडेम रिपीट)**/मिनी उपग्रहों का उपयोग करता है जो व्यक्तियों के बीच प्रतिलिपि संख्या में भिन्न होते हैं। चरण: डीएनए निकालें → प्रतिबंध पाचन → वैद्युतकणसंचलन → दक्षिणी धब्बा → वीएनटीआर जांच के साथ संकरण → ऑटोरैडियोग्राफी → अद्वितीय बैंड पैटर्न।',
            },
            {
              english: 'Steps: extract DNA → restriction digestion → electrophoresis → Southern blot → hybridise with VNTR probe → autoradiography → unique band pattern.',
        hindi: 'चरण: डीएनए निकालें → प्रतिबंध पाचन → वैद्युतकणसंचलन → दक्षिणी धब्बा → वीएनटीआर जांच के साथ संकरण → ऑटोरैडियोग्राफी → अद्वितीय बैंड पैटर्न।',
            },
            {
              english: 'Uses: forensic identification, paternity testing, evolutionary biology, conservation.',
        hindi: 'उपयोग: फोरेंसिक पहचान, पितृत्व परीक्षण, विकासवादी जीव विज्ञान, संरक्षण।',
            },
          ],
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: 'A = T,\\ G \\equiv C \\text{ (Chargaff)}',
      desc: {
        english: '',
        hindi: 'ए = टी,\\ जी सी (चारगफ)',
      },
    },
    {
      latex: '\\text{Length of DNA} = \\text{bp} \\times 3.4\\ \\AA',
      desc: {
        english: '',
        hindi: 'DNA की लंबाई = bp 3.4\\',
      },
    },
    {
      latex: '\\text{Replication: 5}^\\prime \\to 3^\\prime \\text{; Okazaki fragments on lagging strand}',
      desc: {
        english: '',
        hindi: 'प्रतिकृति: 5 3 ; लैगिंग स्ट्रैंड पर ओकाज़ाकी के टुकड़े',
      },
    },
    {
      latex: '\\text{Transcription: template strand 3}^\\prime \\to 5^\\prime',
      desc: {
        english: '',
        hindi: 'प्रतिलेखन: टेम्पलेट स्ट्रैंड 3 5',
      },
    },
    {
      latex: '\\text{64 codons = 61 sense + 3 stop; AUG = start}',
      desc: {
        english: '',
        hindi: '64 कोडन = 61 इंद्रिय + 3 स्टॉप; अगस्त = प्रारंभ',
      },
    },
    {
      latex: '\\text{Peptidyl transferase = ribozyme (23S rRNA)}',
      desc: {
        english: '',
        hindi: 'पेप्टिडिल ट्रांसफ़ेज़ = राइबोज़ाइम (23S rRNA)',
      },
    },
    {
      latex: '\\text{lac operon: i, P, O, z, y, a}',
      desc: {
        english: '',
        hindi: 'लैक ऑपेरॉन: आई, पी, ओ, जेड, वाई, ए',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'NEET',
      tag: 'Replication',
      problem: {
        english: 'Okazaki fragments are joined by:',
        hindi: 'ओकाज़ाकी टुकड़े इससे जुड़े हुए हैं:',
      },
      answer: {
        english: 'DNA ligase',
        hindi: 'डीएनए लिगेज',
      },
      solution: {
        english: 'Seals nicks on lagging strand.',
        hindi: 'लैगिंग स्ट्रैंड पर सील सील।',
      },
    },
    {
      year: 2023,
      exam: 'NEET',
      tag: 'DNA',
      problem: {
        english: 'In a DNA molecule with 1000 bp, the length is approximately:',
        hindi: '1000 बीपी वाले डीएनए अणु में, लंबाई लगभग है:',
      },
      answer: {
        english: '3400 Å (340 nm)',
        hindi: '3400 Å (340 एनएम)',
      },
      solution: {
        english: '1000 × 3.4 Å = 3400 Å.',
        hindi: '1000 × 3.4 Å = 3400 Å.',
      },
    },
    {
      year: 2022,
      exam: 'NEET',
      tag: 'Code',
      problem: {
        english: 'Stop codons are:',
        hindi: 'स्टॉप कोडन हैं:',
      },
      answer: {
        english: 'UAA, UAG, UGA',
        hindi: 'यूएए, यूएजी, यूजीए',
      },
      solution: {
        english: 'Do not code for any amino acid; signal release.',
        hindi: 'किसी भी अमीनो एसिड के लिए कोड न करें; सिग्नल रिलीज.',
      },
    },
    {
      year: 2021,
      exam: 'NEET',
      tag: 'Transcription',
      problem: {
        english: 'In eukaryotes, mRNA is synthesised by:',
        hindi: 'यूकेरियोट्स में, mRNA का संश्लेषण किसके द्वारा किया जाता है:',
      },
      answer: {
        english: 'RNA Polymerase II',
        hindi: 'आरएनए पोलीमरेज़ II',
      },
      solution: {
        english: 'Pol I → rRNA, Pol III → tRNA & 5S.',
        hindi: 'पोल I → rRNA, पोल III → tRNA और 5S।',
      },
    },
    {
      year: 2020,
      exam: 'NEET',
      tag: 'lac operon',
      problem: {
        english: 'The inducer of lac operon is:',
        hindi: 'लैक ऑपेरॉन का प्रेरक है:',
      },
      answer: {
        english: 'Allolactose (lactose isomer)',
        hindi: 'एलोलैक्टोज (लैक्टोज आइसोमर)',
      },
      solution: {
        english: 'Binds repressor and inactivates it.',
        hindi: 'दमनकर्ता को बांधता है और उसे निष्क्रिय कर देता है।',
      },
    },
  ],
  next: {
    slug: 'evolution',
    title: 'Evolution',
  },
};

export default chapter;
